import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { checkStatus, createHeaders, get, getTjenesterApiUrl, HelsenorgeHttpError, HelsenorgeProxyError, } from './hn-proxy-service';
import { createQueryString } from './hn-service';
import resources from './resources/HN.CoreFrontend.NPM.Packages.FrameworkUtils.nb-NO.json';
// Type-guard for å sjekke om error er av typen SerializedProxyError
export const isSerializedProxyError = (error) => {
    return typeof error === 'object' && error !== null && error.type === 'HelsenorgeProxyError';
};
// Type-guard for å sjekke om error er av typen SerializedHttpError
export const isSerializedHttpError = (error) => {
    return typeof error === 'object' && error !== null && error.type === 'HelsenorgeHttpError';
};
// Type-guard for å sjekke om error er av typen FETCH_ERROR (nettverksfeil) eller TIMEOUT_ERROR fra RTK Query
export const isFetchError = (error) => {
    return (typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR'));
};
// Konverterer feil til serialiserte feil-objekter som kan brukes av Redux
const mapErrorToSerializedError = (error) => {
    if (error instanceof HelsenorgeProxyError) {
        return {
            error: {
                type: 'HelsenorgeProxyError',
                message: error.message,
                statusCode: error.statusCode,
                correlationId: error.correlationId,
                response: error.response,
            },
        };
    }
    if (error instanceof HelsenorgeHttpError) {
        return {
            error: {
                type: 'HelsenorgeHttpError',
                message: error.message,
                statusCode: error.statusCode,
                correlationId: error.correlationId,
                response: error.response,
            },
        };
    }
    if (error instanceof Error) {
        return { error: { name: error.name, message: error.message, stack: error.stack } };
    }
    return { error: { message: resources.technical_error_message } };
};
/**
 * Hjelpemetode for å sende get-requester til proxy-API sammen med createApi fra RTK
 * @param args
 * @returns
 */
export const getBaseQuery = async (args) => {
    try {
        const response = await get(...args);
        return { data: response };
    }
    catch (error) {
        return mapErrorToSerializedError(error);
    }
};
/**
 * Hjelpemetode for å lage en fetchBaseQuery til bruk med createApi fra RTK Query
 * @param proxyName navn på api-et/løsningsområdet. Eks pasientjournal eller velgbehandlingssted
 */
export const createFetchBaseQuery = (proxyName) => {
    const baseUrl = getTjenesterApiUrl(proxyName);
    const rawBaseQuery = fetchBaseQuery({
        baseUrl,
        credentials: 'include',
        paramsSerializer: params => createQueryString(params, { includePrefix: false }),
        // Vi håndterer selv status-koder og feilresponser i checkStatus senere
        responseHandler: response => Promise.resolve(response),
        validateStatus: () => true,
    });
    const customBaseQuery = async (args, api, extraOptions) => {
        const request = typeof args === 'string' ? { url: args } : args;
        const method = request.method ?? 'GET';
        const headers = createHeaders('application/json', method);
        new Headers(request.headers).forEach((value, key) => {
            headers.set(key, value);
        });
        const result = await rawBaseQuery({ ...request, headers }, api, extraOptions);
        // Nettverksfeil eller andre feil under fetch
        if (result.error && isFetchError(result.error)) {
            return {
                error: { name: result.error.status, message: resources.technical_error_message },
            };
        }
        const response = result.data;
        try {
            const data = await checkStatus(response);
            return { data };
        }
        catch (error) {
            return mapErrorToSerializedError(error);
        }
    };
    return customBaseQuery;
};
